import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Sparkles, Copy, Check, X, Twitter, Mail, MessageCircle, Share2, PartyPopper } from "lucide-react";
import api from "@/lib/api";

/**
 * Post-checkout referral celebration modal.
 *
 * Fires the "should we show?" check itself so the caller only needs to mount
 * this component whenever a paid checkout completes — the backend rules
 * (2-impression cap, membership vs. every-3rd-order) decide whether it
 * actually appears.
 *
 * Props:
 *  - kind: "membership" | "founding1" | "drop" | "order"
 */
export default function ReferralCelebrationModal({ kind = "order" }) {
  const [state, setState] = useState({ status: "checking" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/referrals/celebration-state?kind=${encodeURIComponent(kind)}`)
      .then(r => { if (!cancelled) setState({ status: r.data.should_show ? "open" : "hidden", ...r.data }); })
      .catch(() => { if (!cancelled) setState({ status: "hidden" }); });
    return () => { cancelled = true; };
  }, [kind]);

  const inviteUrl = useMemo(() => {
    if (!state.data) return "";
    return `${window.location.origin}${state.data.invite_url_suffix}`;
  }, [state.data]);

  const close = async () => {
    setState(s => ({ ...s, status: "hidden" }));
    try { await api.post("/referrals/celebration-seen", { dismissed: true }); } catch (e) { /* ignore */ }
  };

  const track = async (channel) => {
    try { await api.post("/referrals/share-event", { channel }); } catch (e) { /* ignore */ }
  };

  const shareText = useMemo(() => {
    if (!state.data) return "";
    return `I just got a box from The Farm To You — real ranchers, real meat, delivered frozen to my door. Take $${state.data.reward_per_conversion} off yours with my link:`;
  }, [state.data]);

  if (state.status !== "open" || !state.data) return null;

  const mode = state.mode || "first";
  const isRepeat = mode === "repeat";

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      track("copy");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* ignore */ }
  };
  const doTwitter = () => {
    track("twitter");
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(inviteUrl)}`;
    window.open(url, "_blank", "noopener,width=600,height=500");
  };
  const doEmail = () => {
    track("email");
    const subject = "Real meat, delivered — take $10 off your first box";
    const body = `${shareText}\n\n${inviteUrl}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const doSms = () => {
    track("sms");
    const body = `${shareText} ${inviteUrl}`;
    window.location.href = `sms:?&body=${encodeURIComponent(body)}`;
  };
  const doNative = async () => {
    track("native");
    if (navigator.share) {
      try {
        await navigator.share({ title: "The Farm To You", text: shareText, url: inviteUrl });
      } catch (e) { /* user-cancelled */ }
    } else {
      doCopy();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        data-testid="referral-celebration-modal"
        onClick={close}
      >
        <motion.div
          key="card"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: "spring", stiffness: 180, damping: 22 }}
          className="relative bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Confetti burst — pure CSS/motion, no new dep */}
          <ConfettiBurst />

          <button
            onClick={close}
            className="absolute top-4 right-4 text-stone-400 hover:text-stone-700 transition-colors z-10"
            data-testid="celebration-close"
            aria-label="Close"
          >
            <X size={20}/>
          </button>

          <div className="relative z-10 text-center">
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.15, type: "spring", stiffness: 220 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 text-[var(--ff-primary)] mb-4"
            >
              {isRepeat ? <Sparkles size={30}/> : <PartyPopper size={30}/>}
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
              className="overline text-[var(--ff-primary)]" data-testid="celebration-eyebrow"
            >
              {isRepeat ? "You're on a roll" : "Give $10 — get $10"}
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="font-display text-3xl mt-2 leading-tight" data-testid="celebration-headline"
            >
              {isRepeat
                ? "Keep the flywheel going."
                : "Your box is locked in. Let's celebrate."}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              className="text-[var(--ff-text-secondary)] mt-3 text-sm leading-relaxed"
            >
              {isRepeat ? (
                <>You&apos;ve already invited <span className="font-semibold text-[var(--ff-text-primary)]">{state.data.referees_converted}</span>. Get one more friend on board and earn another <span className="font-semibold text-[var(--ff-primary)]">${state.data.reward_per_conversion}</span> off your next box.</>
              ) : (
                <>For every friend who joins with your code, you both get <span className="font-semibold text-[var(--ff-primary)]">${state.data.reward_per_conversion}</span> off — no cap on how much you can stack.</>
              )}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="mt-6 bg-stone-50 border border-stone-200 rounded-xl p-3 flex items-center justify-between gap-3"
              data-testid="celebration-link-row"
            >
              <div className="text-xs text-left truncate flex-1 font-mono text-[var(--ff-text-secondary)]">
                {inviteUrl}
              </div>
              <button
                onClick={doCopy}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--ff-primary)] hover:text-[var(--ff-primary-dark)] whitespace-nowrap"
                data-testid="celebration-copy"
              >
                {copied ? <><Check size={14}/> Copied</> : <><Copy size={14}/> Copy link</>}
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              className="mt-4 grid grid-cols-4 gap-2"
            >
              <ShareBtn onClick={doTwitter} icon={Twitter} label="X" testid="celebration-twitter" tone="sky"/>
              <ShareBtn onClick={doSms} icon={MessageCircle} label="Text" testid="celebration-sms" tone="emerald"/>
              <ShareBtn onClick={doEmail} icon={Mail} label="Email" testid="celebration-email" tone="rose"/>
              <ShareBtn onClick={doNative} icon={Share2} label="More" testid="celebration-native" tone="amber"/>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.75 }}
              className="mt-5 text-xs text-[var(--ff-text-muted)]"
              data-testid="celebration-code-line"
            >
              Your code: <span className="font-mono font-semibold tracking-wider">{state.data.code}</span>
              {state.data.credits_usd > 0 && (
                <> · <span className="text-emerald-700 font-semibold">${state.data.credits_usd.toFixed(2)}</span> in referral credits</>
              )}
            </motion.p>

            <motion.button
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }}
              onClick={close}
              className="mt-6 text-xs text-[var(--ff-text-muted)] hover:text-[var(--ff-text-primary)]"
              data-testid="celebration-later"
            >
              Maybe later
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ShareBtn({ onClick, icon: Icon, label, testid, tone }) {
  const bg = {
    sky: "hover:bg-sky-50 hover:border-sky-200 hover:text-sky-700",
    emerald: "hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700",
    rose: "hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700",
    amber: "hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700",
  }[tone] || "";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex flex-col items-center justify-center gap-1 py-3 rounded-lg border border-stone-200 bg-white text-[var(--ff-text-secondary)] text-xs font-semibold transition-colors ${bg}`}
    >
      <Icon size={18}/>
      {label}
    </button>
  );
}

// Small confetti burst — 12 dots that fly out from the center on mount.
function ConfettiBurst() {
  const colors = ["#F4A261", "#2A9D8F", "#E76F51", "#E9C46A", "#264653", "#F4A261"];
  const dots = Array.from({ length: 14 });
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      {dots.map((_, i) => {
        const angle = (i / dots.length) * Math.PI * 2;
        const dist = 120 + Math.random() * 60;
        return (
          <motion.span
            key={i}
            initial={{ x: 0, y: 0, opacity: 1, scale: 0 }}
            animate={{
              x: Math.cos(angle) * dist,
              y: Math.sin(angle) * dist - 40,
              opacity: 0,
              scale: 1,
              rotate: Math.random() * 720,
            }}
            transition={{ duration: 1.4, delay: 0.1 + i * 0.02, ease: "easeOut" }}
            style={{
              background: colors[i % colors.length],
              left: "50%",
              top: "42%",
            }}
            className="absolute w-2 h-2 rounded-sm"
          />
        );
      })}
    </div>
  );
}
